import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';

import { contentTypeFilterSelected, filterContentType } from '../../../actions/ContentExplorerFilterActions';
import { ContentTypeFilterKey } from '../../../models/ContentFilters';
import { ExplorableItemType } from '../../../models/ContentType';
import { selectDefaultContentTypeFilterValues } from '../../../selectors/ContentFilterSelectors';
import { getContentTypesFromQueryParam } from '../ExplorerUrlBindings';

export interface ContentTypeFilterUpdaterOwnProps {
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export interface ContentTypeFilterUpdaterStateProps {
  readonly defaultContentTypeValues: ReadonlyArray<ExplorableItemType>;
}

export interface ContentTypeFilterUpdaterDispatchProps {
  readonly filterContentType: (contentTypes: ReadonlyArray<ExplorableItemType>) => void;
  readonly contentTypeFilterSelected: (contentTypes: ReadonlyArray<ExplorableItemType>, clearAndSet: boolean) => void;
}

export type ContentTypeFilterUpdaterProps = ContentTypeFilterUpdaterOwnProps
  & ContentTypeFilterUpdaterStateProps
  & ContentTypeFilterUpdaterDispatchProps;

/**
 * Component that syncs the content types from the URL query params to the Redux store before rendering its children.
 */
export class ContentTypeFilterUpdater extends React.Component<ContentTypeFilterUpdaterProps> {
  constructor(props: ContentTypeFilterUpdaterProps) {
    super(props);
    props.filterContentType(this.getContentTypesFromQuery());
  }

  render() {
    return <>{this.props.children}</>;
  }

  componentDidUpdate(prevProps: ContentTypeFilterUpdaterProps) {
    const contentTypes = this.getContentTypesFromQuery();

    // If resetStore is set, dispatch the filterContentType action and nothing else
    // TFSID:983648.  Handle case when updating this component with a new projectId
    if (this.props.resetStore) {
      this.props.filterContentType(contentTypes);
    } else {
      const curQueryValues = queryString.parse(this.props.routeLocationSearch);
      const prevQueryValues = queryString.parse(prevProps.routeLocationSearch);

      if (prevQueryValues[ContentTypeFilterKey] === curQueryValues[ContentTypeFilterKey]) return;

      if (prevProps.routeLocationPath === this.props.routeLocationPath) {
        this.props.contentTypeFilterSelected(contentTypes, null);
      }
    }
  }

  private getContentTypesFromQuery() {
    const queryParams = queryString.parse(this.props.routeLocationSearch);
    return getContentTypesFromQueryParam(queryParams[ContentTypeFilterKey], this.props.defaultContentTypeValues);
  }
}

const mapStateToProps: MapStateToProps<ContentTypeFilterUpdaterStateProps> = state => ({
  defaultContentTypeValues: selectDefaultContentTypeFilterValues(state),
});

const mapDispatchToProps: MapDispatchToProps<ContentTypeFilterUpdaterDispatchProps> = dispatch => bindActionCreators({
  filterContentType,
  contentTypeFilterSelected,
}, dispatch);

export const ConnectedContentTypeFilterUpdater = connect(mapStateToProps, mapDispatchToProps)(ContentTypeFilterUpdater);

/**
 * Creates a higher order component that wraps the given component with the ContentTypeFilterUpdater component.
 */
export function withContentTypeFilterUpdater<P>(Component: React.ComponentType<P>) {
  return (props: P & ContentTypeFilterUpdaterOwnProps) => (
    <ConnectedContentTypeFilterUpdater {...props}><Component {...props}/></ConnectedContentTypeFilterUpdater>
  );
}
